import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '../../lib/api'
import { FEATURE_SURVEY_KEYS } from '../shared/featureSurvey/types'

export interface CreditBucket {
  source: string
  total: number
  /** @deprecated Provenance-only; expiry is on ``expiring_next``. */
  soonest_expiry?: string | null
  soonest_expiry_date?: string | null
}

export interface ExpiringNext {
  points: number
  expires_at: string
  expires_at_date?: string | null
}

export interface SubscriptionSummary {
  tier_code: string
  display_name: string
  currency?: string
  amount_net_minor?: number
  amount_net_grosz: number
  points_granted: number
  renewal_date: string | null
  is_grandfathered: boolean
  list_amount_net_minor?: number | null
  list_amount_net_grosz: number | null
  billing_status?: string
  billing_interval?: 'month' | 'year' | string
}

export interface CreditBalance {
  total: number
  buckets: CreditBucket[]
  soonest_expiry: string | null
  soonest_expiry_date?: string | null
  expiring_next?: ExpiringNext | null
  subscription?: SubscriptionSummary | null
}

export const CREDIT_BALANCE_KEY = ['creditBalance'] as const

export const useCreditBalance = () => {
  return useQuery<CreditBalance>({
    queryKey: CREDIT_BALANCE_KEY,
    queryFn: async () => {
      const res = await api.get('/credits/balance')
      return res.data
    },
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
}

/**
 * Returns a stable callback that invalidates the credit balance cache.
 * Call this after any task that deducts or grants credits so the header
 * badge and /credits page reflect the new balance immediately.
 *
 * Usage:
 *   const invalidateBalance = useInvalidateCreditBalance()
 *   // inside task completion handler:
 *   invalidateBalance()
 */
export const useInvalidateCreditBalance = () => {
  const queryClient = useQueryClient()
  return useCallback(() => {
    // Invalidate balance (header badge, /credits page)
    queryClient.invalidateQueries({ queryKey: CREDIT_BALANCE_KEY })
    // Invalidate cost-preview queries so CreditActionButton reflects updated balance/sufficiency
    queryClient.invalidateQueries({ queryKey: ['creditCost'] })
    // Re-check NPS eligibility after points are spent
    queryClient.invalidateQueries({ queryKey: FEATURE_SURVEY_KEYS.status })
  }, [queryClient])
}
